import { useMemo, useRef, useState } from 'react';
import {
  useGetAllEventsQuery,
  useGetPublicCategoriesQuery,
  useGetPublicCategoryEventsQuery,
} from '../store';
import LandingEventCard from '../components/LandingEventCard';
import EventDTO, { PublicCategoryDTO } from '../models/event';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import AvailableEventsPageSkeleton from '../components/AvailableEventsPageSkeleton';
import FeaturedEventsBanner from '../components/FeaturedEventsBanner/FeaturedEventsBanner';
import CategoryFilterPills from '../components/CategoryFilterPills/CategoryFilterPills';
import ThisWeekEvents from '../components/ThisWeekEvents/ThisWeekEvents';
import { BusinessDTO } from '../models/business';
import BusinessMemberCards from '../components/BusinessMemberCards';
import { IconEnjoy1 } from '../components/icons';
import CardsLottie from '../components/CardsLottie';
import usePageMetadata from '../hooks/usePageMetadata';
import useSiteBusiness from '../hooks/useSiteBusiness';
import compareEventsByPriority from '../utils/compareEventsByPriority';
import buildFeaturedSlides, { buildFeaturedBusinessGroups } from '../utils/buildFeaturedSlides';
import FeaturedBusinessCard from '../components/FeaturedBusinessCard/FeaturedBusinessCard';
import { FEATURED_BUSINESSES } from '../config/featuredBusinesses';
import HomeSearch from '../components/HomeSearch/HomeSearch';
import {
  EventSearchFilter, SUPPORT_WHATSAPP_URL, describeFilter, searchEvents,
} from '../utils/eventSearch';

interface Props {
  loadedEvents?: EventDTO[];
  loadedCategories?: PublicCategoryDTO[];
  business?: BusinessDTO;
  onNavigate?: (path: string) => void;
}

const AvailableEventsPage: React.FC<Props> = ({
  loadedEvents, loadedCategories, business: businessProp, onNavigate,
}) => {
  const navigateWithQuery = useNavigateWithQuery();
  const {
    business: businessFromHost, isLoading: isBusinessLoading, isProducerSite,
  } = useSiteBusiness(!!businessProp);
  const business = businessProp ?? businessFromHost;

  // On a producer host (custom domain or subdomain) we never show the global
  // feed. `isProducerSite` is known synchronously from the hostname, so we skip
  // the all-events query entirely — avoiding both the wasted fetch of every
  // producer's events and the "all events → business events" flash. The main
  // site still fires it so cache invalidations refresh the list.
  const { data: events, isLoading } = useGetAllEventsQuery({}, { skip: isProducerSite });

  // Skip the client query when SSR has already provided categories; the
  // RTK cache will still pick them up on subsequent navigations.
  const { data: categoriesRes } = useGetPublicCategoriesQuery(undefined, { skip: !!loadedCategories });
  // Hide categories that have no live events — clicking a chip with zero
  // events leads to an empty grid (bad UX).
  const categories = loadedCategories ?? (categoriesRes?.data ?? []).filter((c) => c.event_count > 0);

  const [activeCategorySlug, setActiveCategorySlug] = useState<string | undefined>(undefined);
  // Buscador de la home (AT-1804): "Ver todos" filtra la grilla de abajo.
  const [searchFilter, setSearchFilter] = useState<EventSearchFilter | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { data: categoryEventsRes, isFetching: isFetchingCategoryEvents } = useGetPublicCategoryEventsQuery(
    { categorySlug: activeCategorySlug ?? '' },
    { skip: !activeCategorySlug },
  );

  // On a landing show only the producer's own events (already fetched with the
  // business); on the main site prefer the live RTK result, falling back to SSR
  // data while it loads.
  const eventsToShow = isProducerSite
    ? (business?.events ?? [])
    : (events ?? loadedEvents);

  // Same ordering as the banner: featured events (higher `priority`) first,
  // the rest by date.
  const sortedEvents = useMemo(() => {
    if (!eventsToShow) {
      return [];
    }
    return [...eventsToShow]
      .filter((e) => e.public_display)
      .sort(compareEventsByPriority);
  }, [eventsToShow]);

  const displayedEvents = useMemo(() => {
    if (!activeCategorySlug) {
      return sortedEvents;
    }
    const categoryEvents = [...(categoryEventsRes?.data ?? [])]
      .filter((e) => e.public_display)
      .sort(compareEventsByPriority);
    // Keep category filtering scoped to the business on a landing.
    return business
      ? categoryEvents.filter((e) => e.business_id === business.id)
      : categoryEvents;
  }, [activeCategorySlug, categoryEventsRes, sortedEvents, business]);

  const searchedEvents = useMemo(
    () => (searchFilter ? searchEvents(displayedEvents, searchFilter) : displayedEvents),
    [displayedEvents, searchFilter],
  );

  // "Esta semana" = events from today (00:00) through the upcoming Sunday
  // (23:59:59). When today IS Sunday, the window collapses to just today.
  // We need at least this many events for the dedicated section to be useful;
  // otherwise those events just stay in the main "Próximos eventos" grid.
  const MIN_WEEK_EVENTS = 2;
  const weekEvents = useMemo(() => {
    if (displayedEvents.length === 0) {
      return [];
    }
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfToday);
    endOfWeek.setDate(startOfToday.getDate() + ((7 - startOfToday.getDay()) % 7));
    endOfWeek.setHours(23, 59, 59, 999);
    return displayedEvents.filter((e) => {
      const eventDate = new Date(e.date);
      return eventDate >= startOfToday && eventDate <= endOfWeek;
    });
  }, [displayedEvents]);

  // Featured businesses (a festival with several nights) show up once on the
  // main site: one banner slide and one card, first, in the grid and in "Esta
  // semana", instead of one per night. Never on a producer's own landing.
  const featuredBusinesses = business ? [] : FEATURED_BUSINESSES;

  const groupOut = (list: EventDTO[]) => {
    const groups = buildFeaturedBusinessGroups(list, featuredBusinesses);
    const groupedIds = new Set(groups.map((g) => g.businessId));
    return { groups, rest: list.filter((e) => !groupedIds.has(e.business_id)) };
  };

  const grid = useMemo(() => groupOut(searchedEvents), [searchedEvents, business]);
  const week = useMemo(() => groupOut(weekEvents), [weekEvents, business]);

  const showWeekSection = !searchFilter && week.groups.length + week.rest.length >= MIN_WEEK_EVENTS;

  const featuredSlides = useMemo(
    () => buildFeaturedSlides(sortedEvents, featuredBusinesses),
    [sortedEvents, business],
  );

  const navigateTo = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigateWithQuery(path);
    }
  };

  const handleEventSelect = (eventId: string) => {
    if (eventId) {
      navigateTo(`/events/${eventId}`);
    }
  };

  // Only on the main site: a producer landing already shows few events.
  const showSearch = !business && sortedEvents.length > 0;

  const handleShowAllResults = (filter: EventSearchFilter) => {
    setSearchFilter(filter);
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Solo para el sitio de una productora (subdominio o dominio propio), que se
  // resuelve en el cliente. Para avantti.com.ar NO se toca: la metadata la
  // define el server en src/app/page.tsx y este hook la pisaba al hidratar,
  // dejando el title generico "Proximos eventos | Avantti" en la pagina mas
  // importante del sitio. Google indexa el DOM renderizado, asi que se estaba
  // tirando a la basura el title optimizado.
  usePageMetadata({
    title: business?.name ? `Eventos de ${business.name}` : undefined,
    description: business?.name
      ? `Eventos de ${business.name}. Comprá tus entradas en Avantti.`
      : undefined,
  });

  return (
    <div className="flex flex-col gap-6 py-5 h-full min-h-screen">
      {((isLoading && !loadedEvents) || (isProducerSite && isBusinessLoading))
        && <AvailableEventsPageSkeleton />}
      {showSearch && (
        <HomeSearch
          events={sortedEvents}
          onEventSelect={handleEventSelect}
          onShowAll={handleShowAllResults}
        />
      )}

      {featuredSlides.length !== 0 && (
        <aside data-nosnippet="">
          <FeaturedEventsBanner
            slides={featuredSlides}
            onSlideClick={navigateTo}
          />
        </aside>
      )}

      {!business?.is_white_label && <div>
        <CardsLottie />
      </div>}

      {!business?.is_white_label && categories.length > 0 && (
        <div className="sticky top-[4.8rem] z-30 -mx-2 lg:-mx-6 bg-blur-lg bg-opacity-heavy border-b border-border-secondary [will-change:transform]">
          <div className="px-2 lg:px-6 py-3">
            <CategoryFilterPills
              categories={categories}
              activeSlug={activeCategorySlug}
              onSelect={setActiveCategorySlug}
            />
          </div>
        </div>
      )}

      {loadedEvents && business?.has_members
        && <BusinessMemberCards business={business} />}

      {showWeekSection && (
        <ThisWeekEvents
          events={week.rest}
          onEventSelect={handleEventSelect}
          leading={week.groups.map((g) => (
            <div key={g.key} className="snap-start">
              <FeaturedBusinessCard group={g} onSelect={navigateTo} />
            </div>
          ))}
        />
      )}

      {sortedEvents.length > 0 && (
        <>
          <div ref={resultsRef} className="flex flex-row flex-wrap items-center gap-4 scroll-mt-40">
            <h1 className="text-xl-semibold lg:display-md-semibold text-text-primary m-0">
              {business?.name
                ? `Próximos eventos de ${business.name}`
                : 'Próximos eventos'}
            </h1>
            <IconEnjoy1 className="w-10 h-10" />
            {searchFilter && (
              <div className="flex items-center gap-3 w-full lg:w-auto lg:ml-auto">
                <span className="text-md-medium text-text-secondary">
                  {`${searchedEvents.length === 1 ? '1 resultado' : `${searchedEvents.length} resultados`} para ${describeFilter(searchFilter)}`}
                </span>
                <button
                  type="button"
                  onClick={() => setSearchFilter(null)}
                  className="h-10 px-4 rounded-full border border-border-secondary bg-surface-base text-sm font-semibold text-text-primary hover:border-primary-300 hover:text-primary-500 transition-colors"
                >
                  Limpiar búsqueda
                </button>
              </div>
            )}
          </div>
          {searchFilter && searchedEvents.length === 0 && (
            <p className="text-text-secondary text-md-medium">
              No hay eventos que coincidan con la búsqueda.
              {' '}
              <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noreferrer" className="text-primary-500 font-semibold">
                Pedir ayuda por WhatsApp
              </a>
            </p>
          )}
          {!(searchFilter && searchedEvents.length === 0) && (activeCategorySlug && displayedEvents.length === 0 && !isFetchingCategoryEvents ? (
            <p className="text-text-secondary text-md-medium">
              No hay eventos en esta categoría todavía.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-20 gap-6 items-center flex-wrap">
              {grid.groups.map((g) => (
                <div key={g.key}>
                  <FeaturedBusinessCard group={g} onSelect={navigateTo} />
                </div>
              ))}
              {grid.rest.map((event) => (
                <div key={event.id}>
                  <LandingEventCard
                    event={event}
                    onSelect={handleEventSelect}
                    textToast={event.is_sold_out ? 'Entradas agotadas' : undefined}
                    colorToast={event.is_sold_out ? 'bg-warning-300' : undefined}
                    category={event.categories?.[0]}
                  />
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default AvailableEventsPage;
